<template>
  <main role="main">
    <public-banner class="banner" />
    <div class="policy-container">
      <div class="container-content">
        <section class="privacy">
          <h1 v-t>Privacy Policy</h1>
        </section>
        <section class="privacy-content">
          <div class="policy-sections">
            <ul>
              <li><a v-t href="#privacy-notice">Privacy Notice</a></li>
              <li>
                <a v-t href="#online-mobile-privacy-policy">Online and Mobile Privacy Policy</a>
              </li>
              <li><a v-t href="#contact-us">Contact us</a></li>
            </ul>
          </div>
          <div class="policy-sections">
            <p>
              <strong v-t>
                Wealthbar Financial Services Inc., doing business as "CI Direct Investing",
                "WealthBar", "Assante Connect" or "PPI Valet"
              </strong>
            </p>
          </div>
          <div class="policy-sections">
            <h2 id="privacy-notice" v-t>Privacy Notice</h2>
            <h3 v-t>Your Privacy</h3>
            <p v-t>
              We are dedicated to respecting and protecting the privacy and
              confidentiality of the information you have entrusted to us. This Privacy
              Notice outlines our privacy principles and practices.
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>Scope</h3>
            <p>
              <strong v-t>What is personal information?</strong>
              <br>
              <span v-t>
                Personal information refers to information that identifies you. The word
                "information" means personal, financial and other details about you that
                you, or someone you authorize, provide to us or we obtain from others
                within or outside the Company.
              </span>
            </p>
            <p>
              <strong v-t>What does it apply to?</strong>
              <br>
              <span v-t>
                This Privacy Notice applies to the operations of Wealthbar Financial
                Services Inc., doing business as CI Direct Investing, WealthBar, Assante
                Connect or PPI Valet in Canada. It applies regardless of how you choose to
                do business with us – over the phone, in person, through the internet,
                through your mobile device or through email.
              </span>
            </p>
            <p>
              <strong v-t>Who does it apply to?</strong>
              <br>
              <span v-t>
                The Notice applies to all individuals whose personal information we
                collect, including clients who are individuals, individuals carrying on
                business alone or in partnership with other individuals, signing officers
                of our business clients, and beneficiaries of clients to the extent that
                we collect, use or disclose their personal information.
              </span>
            </p>
            <p>
              <strong v-t>Changes to the notice</strong>
              <br>
              <span v-t>
                We may amend this Notice from time to time and will post the revised
                Privacy Notice on our website.
              </span>
            </p>
            <p>
              <strong v-t>Your consent</strong><br>
              <span v-t>
                Each time you use our online, mobile, and electronic services, you are
                indicating your acknowledgment and consent to the collection, use and
                disclosure of your personal information as identified in this policy,
                which we may revise periodically without notice. We will let you know of
                any changes by posting the revised policy with a new effective date. If
                you do not accept the terms outlined in this policy, please do not use our
                online, mobile and electronic services.
              </span>
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>Our Privacy Principles</h3>
            <p v-t>
              The Privacy Notice outlines our privacy principles and informs you of the
              practices we have in place to manage personal information. We aim to
              respect and protect your privacy by following the key privacy principles
              below.
            </p>
            <ol>
              <li>
                <strong v-t>Taking accountability</strong> &#8211;
                <span v-t>
                  We are accountable for
                  the personal information under our control by establishing, training our
                  staff in, and enforcing our privacy policies and procedures to promote
                  compliance with these privacy principles.
                </span>
              </li>
              <li>
                <strong v-t>Identifying for you the purpose for our collection, use, or disclosure</strong>
                &#8211;&nbsp;
                <span v-t>
                  We inform you about the purposes for which we collect, use, and
                  disclose your personal information and we collect, use, and disclose
                  your personal information only for those purposes.
                </span>
              </li>
              <li>
                <strong v-t>Obtaining consent and providing choices</strong> &#8211;&nbsp;
                <span v-t>
                  We obtain your consent to collect, use, or disclose personal information
                  and you may at any time revoke that consent, subject to legal and
                  regulatory requirements. In some cases, we may not be able to provide
                  certain services to you if you do not consent to our collection, use or
                  disclosure of the required information to perform those services.
                </span>
              </li>
              <li>
                <strong v-t>Limiting collection</strong> &#8211;&nbsp;
                <span v-t>
                  We limit collection of your personal information to that which we determine is necessary for
                </span>
                &nbsp;
                <a v-t href="#identifying-purpose-collection">the purposes we identify for you</a>.
              </li>
              <li>
                <strong v-t>Limiting use and disclosure</strong> &#8211;&nbsp;
                <span v-t>
                  We do not use or disclose your personal information for
                </span>
                &nbsp;
                <a v-t href="#identifying-purpose-collection">purposes other than those for which it was collected</a>,
                <span v-t>
                  except with your consent or as required by law.
                </span>
              </li>
              <li>
                <strong v-t>Retaining information</strong> &#8211;&nbsp;
                <span v-t>
                  We have policies in place
                  that govern the retention of your personal information, so we keep it
                  only for as long as reasonably necessary to fulfill its intended purpose
                  or to satisfy legal requirements. When we no longer retain your personal
                  information, we have procedures to destroy, delete, erase, or convert it
                  to an anonymous form, in a manner that keeps it safe in the process.
                </span>
              </li>
              <li>
                <strong v-t>Keeping information accurate</strong> &#8211;&nbsp;
                <span v-t>
                  We maintain your personal information in as accurate, complete, and up-to-date form as is
                  needed for its use.
                </span>
              </li>
              <li>
                <strong v-t>Protecting information</strong> &#8211;&nbsp;
                <span v-t>
                  We maintain appropriate
                  security safeguards to protect against unauthorized access, disclosure,
                  copying, use or modification, theft, misuse, or loss of your personal
                  information in our custody or control. These safeguards are appropriate
                  to the sensitivity of the information.
                </span>
              </li>
              <li>
                <strong v-t>Maintaining openness</strong> &#8211;&nbsp;
                <span v-t>
                  We make readily available to our
                  clients, employees and agents information about our policies and
                  procedures relating to our management of personal information. From time
                  to time, we may make changes to this Privacy Notice and our Online and
                  Mobile Privacy Policy. Our Online and Mobile Privacy below is always the
                  most recent version. Please <a href="#contact-us">contact us</a>
                  to answer any question you may have about our Privacy Notice.
                </span>
              </li>
              <li>
                <strong v-t>Accessing or correcting information</strong> &#8211;&nbsp;
                <span v-t>
                  Upon request, we will
                  inform you of the existence, use and disclosure of your personal
                  information and will give you access to that information, with few
                  exceptions.
                </span>
              </li>
            </ol>
          </div>
          <div class="policy-sections">
            <h3 v-t>Other important information</h3>
            <p><a v-t href="#contact-us">Contact us</a></p>
          </div>
          <div class="policy-sections">
            <h3 v-t>1. Taking accountability</h3>
            <p>
              <span v-t>
                We have designated a privacy officer who is accountable for personal
                information under our control and responsible for compliance with this
                Privacy Notice. Please see
              </span>
              &nbsp;
              <a v-t href="#contact-us">here</a>
              &nbsp;
              <span v-t>for information on how to reach the privacy officer.</span>
            </p>
            <p v-t>
              We have strict policies and procedures governing how we deal with your
              personal information. Every employee is responsible for respecting and
              protecting the personal information to which the employee has access.
            </p>
          </div>
          <div class="policy-sections">
            <h3 id="identifying-purpose-collection" v-t>
              2. Identifying purpose for collection, use, or disclosure
            </h3>
            <h4 v-t>Purposes</h4>
            <p v-t>
              We collect, use and disclose personal information responsibly and only for
              the purposes for which it was collected. When we collect personal
              information, we may use it to:
            </p>
            <ul>
              <li>
                <span v-t>Provide and manage products and services you have requested, including;</span>
                <ul>
                  <li v-t>Opening and operating your account</li>
                  <li v-t>Verifying your identity</li>
                  <li v-t>Executing your transactions</li>
                  <li v-t>
                    Recording account information and report account status back to you
                  </li>
                  <li v-t>Responding to any request or questions you may have</li>
                  <li v-t>
                    Providing you with quality personalized client service and support
                  </li>
                </ul>
              </li>
              <li>
                <span v-t>
                  Understand our customers and to develop and tailor our products and services
                </span>
                <ul>
                  <li v-t>Determine suitability of products and services for you</li>
                  <li v-t>
                    Determine your eligibility for certain of our products and services,
                    or products or services of others
                  </li>
                  <li v-t>
                    Communicate with you about products and services that may be of
                    interest
                  </li>
                  <li v-t>Market and advertise to clients and prospective clients</li>
                  <li v-t>Perform data analytics</li>
                </ul>
              </li>
              <li>
                <span v-t>Legal and Regulatory Obligations</span>
                <ul>
                  <li v-t>Provide all required tax reporting</li>
                  <li v-t>
                    Comply with legal, regulatory, and contractual requirements, or as
                    otherwise permitted or required by law
                  </li>
                  <li v-t>
                    Fulfill obligations under federal anti-money laundering and
                    suppression of terrorism legislation
                  </li>
                  <li v-t>
                    Protect our interests, including recovering any debts you may owe us
                  </li>
                  <li v-t>
                    Protect against fraud and other crime and to manage risk, including
                    conducting investigations and proactive crime prevention measures
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <div class="policy-sections">
            <h4 v-t>
              Your personal information may also be used for purposes named in your
              respective application forms and account agreements.
            </h4>
            <p v-t>
              If a new purpose for using your personal information develops, we will
              inform you of that purpose.
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>3. Obtaining consent and providing choices</h3>
            <h4 v-t>Obtaining Consent</h4>
            <p v-t>
              Depending on the type and sensitivity of the personal information and the
              way you choose to interact with us (in person, internet, phone, email,
              etc.), we may obtain your consent in different ways.
            </p>
            <ul>
              <li v-t>
                We obtain your consent when you choose to open an account through our
                online account opening process and provide us with your personal
                information via the online account creation process.
              </li>
              <li v-t>
                We may obtain your express consent verbally, online, or in writing. For
                instance, when we collect your information via our account application
                form, you will formally indicate your consent for us to collect and use
                the information when you sign the application.
              </li>
              <li v-t>
                We may, however, get your implied consent through your use of a product
                or service, or when you approach us to obtain information, inquire or
                apply for products or services from us.
              </li>
              <li v-t>
                Also, we may obtain consent directly from you or by your authorized
                representative, such as your financial planner, advisor or their
                dealership.
              </li>
            </ul>
            <p v-t>
              Before we make information available to third parties, other than an agent
              or service provider who needs it, we will tell you at the time of consent
              or before we make the information available, who those third parties are,
              the kind of information we want to share with them, and why.
            </p>
            <p v-t>
              We are committed to being transparent and providing you with choices about
              how your information is used.
            </p>
          </div>
          <div class="policy-sections">
            <h4 v-t>Revoking Consent</h4>
            <p>
              <span v-t>
                You may withdraw your consent for the collection, use and disclosure of
                your personal information at any time by forwarding a written request to
                the
              </span>
              &nbsp;
              <a v-t href="#contact-us">Privacy Officer</a>.
              &nbsp;
              <span v-t>
                Please include your full
                name, address, telephone number and account number(s) on any
                correspondence to us.
              </span>
            </p>
            <p v-t>
              However there are certain times when you may not withhold or revoke your
              consent including certain legal, regulatory, or contractual requirements.
            </p>
            <p>
              <span v-t>
                We must receive reasonable notice of your request in order to honour your
                consent withdrawal.
              </span>
              <br>
              <span v-t>
                Your decision to withhold or revoke your consent may limit the products
                and services that we may provide to you and may require you to close your
                accounts with us.
              </span>
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>4. Limiting collection</h3>
            <p v-t>
              We only collect the personal information we determine we need for our
              identified purposes.
            </p>
            <p v-t>For instance, we may collect:</p>
            <ul>
              <li v-t>
                Contact Information, including name, address, telephone number, and
                email address
              </li>
              <li v-t>Date of birth to help us identify and authenticate you</li>
              <li v-t>
                Social Insurance Number (SIN), as required to comply with Canada Revenue
                Agency’s reporting requirements and is also used to keep your
                information separate from that of other clients with a similar name
              </li>
              <li v-t>
                Financial Information to ensure that products, services, and advice we
                provide are appropriate for you and the investments you make are
                suitable for you
              </li>
              <li v-t>
                Health Information if you elect to purchase an insurance product, we may
                be required to collect personal health information on behalf of the
                insurance company during the application process. The personal health
                information may include information related to your medical history,
                personal habits, and/or pre-existing conditions. Medical information
                contained within your insurance application will be maintained within
                our records, communicated to the insurance company and used by them to
                assess your application for insurance. If you have further questions
                related to the use and disclosure of your health information, please
                refer to the insurers’ privacy policy prior within the application forms
                prior to completing the application.
              </li>
              <li v-t>
                Device Information such as information about your browser, operating
                system, software applications, IP address, geolocation, security status,
                and other device information to improve your experience, to protect
                against fraud, and to manage risk
              </li>
              <li>
                <span v-t>
                  Website Use Information such as browsing behaviour on our sites and
                  links, location you click, form data and downloads, as well as other
                  data gathered from the use of web tools (for instance, Cookies, Web
                  Beacons, Tagging) to better understand your interests and needs so that
                  we can serve you better. For more information, see our
                </span>
                &nbsp;
                <span v-t><a href="#online-mobile-privacy-policy">Online and Mobile Privacy Policy</a>.</span>
              </li>
              <li v-t>
                Employment Information to open accounts and in order to comply with
                anti-money laundering and other regulations
              </li>
              <li v-t>
                Audio Recordings, when we monitor or record incoming or outgoing
                telephone calls with you to ensure accuracy, security, service quality,
                for training purposes, and to establish a record. If you do not wish to
                have your call recorded, you have other options to conduct business with
                us, such as in person, online, or by communicating with us in writing
              </li>
              <li v-t>
                Video Recordings or Photographs, as we may use video or photo
                surveillance in and around our offices for the safety of our clients and
                employees, and to protect against theft, property damage, and fraud
              </li>
              <li v-t>
                Social Media, when communicating with us through social media, we may
                monitor or record our discussions for mutual protection, to enhance
                client service, and to confirm our discussions with you.
              </li>
              <li>
                <span v-t>
                  Email Information, when establish your account you must provide an email
                  address to allow us to register your account and communicate with you
                  about our products and service. Further if you choose to contact us by
                  email any information included in the email will also be collected and
                  we will use your email address to reply to you and we will store your
                  communication and our reply in case we correspond further.
                </span>
                <br>
                <br>
                <strong v-t>IMPORTANT:</strong>&nbsp;
                <span v-t>
                  We will not ask you to provide personal information or login
                  information, such as username, passwords, PINs, security question and
                  answers or account numbers, through unsolicited email. If you receive an
                  email claiming to be from us that you believe to be fraudulent, do not
                  respond and do not open or click any links or open attachments contained
                  in the email. Please
                </span>
                &nbsp;<a v-t href="#contact-us">notify us immediately</a>&nbsp;
                <span v-t>and then delete the email.</span>
              </li>
            </ul>
          </div>
          <div class="policy-sections">
            <h3 v-t>5. Limiting use and disclosure</h3>
            <h4 v-t>Use</h4>
            <p v-t>
              We only use or disclose your information for the purpose(s) it was
              collected and as otherwise identified in this Notice.
            </p>
          </div>
          <div class="policy-sections">
            <h4 v-t>Disclosure</h4>
            <p v-t>
              Access to personal information is restricted to those staff members who
              need the information to carry out their job duties. Those employees
              maintain the information in the strictest of confidence and do not provide
              access to the information to anyone who is not authorized.
            </p>
            <p v-t>We may provide your information to third parties, including:</p>
            <ul>
              <li v-t>
                To third party service providers for servicing purposes – We disclose to
                them only such personal information as they need to perform the
                services. We do not authorize them to use or disclose the personal
                information for their own marketing or other purposes. We have contracts
                in place holding these companies to the same standards of
                confidentiality by which we are governed. They may be located in Canada
                or other jurisdiction or countries and may disclose information in
                response to valid demands or requests from governments, regulators,
                courts and law enforcement authorities in those jurisdictions or
                countries in accordance with the applicable law in that jurisdiction or
                country.
              </li>
              <li v-t>To your legal representatives</li>
              <li v-t>
                To your financial institutions, securities dealers and mutual fund
                companies
              </li>
              <li v-t>
                To CI Financial company affiliates, We may share personal information
                among any of the subsidiaries and affiliated companies that comprise CI
                Financial Inc. who perform administrative services for us and/or to
                better understand your financial needs and to provide products and
                services we believe may interest you.
              </li>
              <li v-t>
                To governments, government agencies, regulators, including
                self-regulators, when required or permitted to do so by law, including
                in response to a search warrant, court order, or other demand or inquiry
                which we believe to be valid
              </li>
              <li v-t>
                To protect our interests, we may disclose information to any person or
                organization, including an investigative body, in order to prevent,
                detect or suppress, financial abuse, fraud, criminal activity, protect
                our assets and interests, or manage or settle any actual or potential
                loss or in the case of a breach of agreement or contravention of law. We
                may also disclose information to help us collect a debt owed to us
              </li>
              <li v-t>
                In the event of a transfer of a business, we may buy or sell a business
                (or evaluate those transactions) which would result in certain personal
                information forming business assets that would be purchased or sold as
                part of a transfer. We may also transfer personal information as part of
                a corporate reorganization or other change in corporate control
              </li>
              <li>
                <span v-t>
                  In other situations where we have your consent, for instance, sharing
                  your information with a joint account holder
                </span>
                <br>
                <span v-t>
                  We do not sell or rent client lists or personal information to third
                  parties.
                </span>
              </li>
            </ul>
          </div>
          <div class="policy-sections">
            <h3 v-t>6. Retaining information</h3>
            <h4 v-t>Where your information is kept</h4>
            <p v-t>Your personal information is kept:</p>
            <ul>
              <li v-t>In electronic or paper format in our offices</li>
              <li v-t>In secure off-site storage facilities, or</li>
              <li v-t>In the offices of our service providers</li>
            </ul>
            <p v-t>
              We may store your personal information via third party entities that
              reside in foreign jurisdictions. Information may be transferred to,
              accessed and/or retained by these third parties. We have contracts in
              place holding these companies to the same standards of confidentiality by
              which we are governed. We and/or a third party may be compelled, by law or
              otherwise, to provide information to a third party, subject to applicable
              privacy law. We must abide by applicable privacy law in the
              jurisdiction(s) in which we operate.
            </p>
          </div>
          <div class="policy-sections">
            <h4 v-t>Retention</h4>
            <p v-t>
              We have policies in place that govern the retention of your personal
              information so we keep it only for as long as reasonably necessary to
              fulfill its intended purpose or to satisfy legal requirements. The length
              of time we keep your information will depend upon the product or service
              and the nature of the information.
            </p>
          </div>
          <div class="policy-sections">
            <h4 v-t>Deletion</h4>
            <p v-t>
              When we no longer retain your personal information, we have procedures to
              destroy, delete, erase or convert it to an anonymous form, in a manner
              that keeps it safe in the process.
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>7. Keeping information accurate</h3>
            <p v-t>
              We are committed to maintaining the accuracy of your personal information
              and take reasonable efforts to keep your information complete and
              up-to-date. We maintain your personal information in as accurate,
              complete, and up-to-date form as is needed for its use.
            </p>
            <p>
              <span v-t>
                If you discover inaccuracies in our records, or your personal information
                changes, please notify your financial advisor or contact our Client
                Services Department directly at
              </span>
              &nbsp;
              <a href="mailto:support@cidirectinvesting.com">support@cidirectinvesting.com</a>
              &nbsp;
              <span v-t>
                so that we can make the necessary changes. Failure to notify us of changes
                to your personal information may negatively impact the way we communicate
                or provide services to you. Where appropriate, we will advise others of
                any material amendments to your personal information that we may have
                released to them.
              </span>
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>8. Protecting Information</h3>
            <p v-t>
              We maintain appropriate physical, electronic, technological, procedural, and organizational
              safeguards to protect against unauthorized access, disclosure, copying, use or modification,
              theft, misuse, or loss of your personal information in our custody or control.
              These safeguards are appropriate to the sensitivity of the information.
            </p>
            <p>
              <span v-t>
                We have agreements in place with third party service providers requiring that any information
                we provide to them must be safeguarded and used only for the purpose of providing the service
                we have requested the provider to perform. More details are available on the
              </span>&nbsp;
              <a v-t href="https://www.cidirectinvesting.com/security">CI Direct Investing Security page</a>.
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>9. Maintaining openness</h3>
            <p v-t>
              We make readily available to our clients, employees and agents information about our policies
              and procedures relating to our management of personal information.
            </p>
            <p v-t>
              From time to time, we may make changes to this Privacy Notice and our Online and Mobile Privacy
              Policy. Our Online and Mobile Privacy below is always the most recent version.
            </p>
            <p>
              <span v-t>Please see</span>&nbsp;
              <a v-t href="#contact-us">Contact Us</a>&nbsp;
              <span v-t>to answer any question you may have about our Privacy Notice.</span>
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>10. Accessing or correction information</h3>
            <p>
              <span v-t>
                Individuals do not always need to use their right under the law to access or correct their
                personal information with us. Clients are encouraged to contact our Client Services department at
              </span>&nbsp;
              <a v-t href="mailto:support@cidirectinvesting.com">support@cidirectinvesting.com</a>&nbsp;
              <span v-t>
                to correct or access their information. However, you do have the right, subject to certain
                limitations, to formally request to review or verify your personal information, or to find out
                to whom we have disclosed it. To make a formal request for access, send a written request
                addressed to the Privacy Officer in
              </span>&nbsp;
              <a v-t href="#contact-us">Contact Us</a>&nbsp;
              <span v-t>
                below. Please include your full name, address, telephone number, and account number(s) on all
                correspondence to us and provide enough detail to allow us to identify the information you want.
              </span>
            </p>
            <p v-t>
              Upon request, we will provide you with access to your personal information we have on record at the
              time of the request. Please note that we may not be able to provide you with this information in certain
              circumstances, for example without limitation, where such information:
            </p>
            <ul>
              <li v-t>Contains references to other persons</li>
              <li v-t>Contains proprietary information confidential to us or your financial advisor</li>
              <li v-t>Has been destroyed</li>
              <li v-t>Is too costly to retrieve</li>
              <li v-t>Is subject to legal or some other form of privilege</li>
              <li v-t>Cannot be disclosed for legal reasons</li>
              <li v-t>Relates to an investigation</li>
            </ul>
            <p v-t>
              If we are unable to provide some of the personal information, we hold about you, we will tell you
              why where permitted by law.
            </p>
            <p v-t>
              We will advise you in advance if a minimal charge will be required for conducting the search and
              we will respond to your request within 30 days.
            </p>
          </div>
          <div class="policy-sections">
            <h2 id="online-mobile-privacy-policy" v-t>Online and Mobile Privacy Policy</h2>
            <h3 v-t>Scope</h3>
            <p v-t>
              This Online and Mobile Privacy Policy supplements the Privacy Notice and
              specifically addresses the way in which we collect, use, disclose, and
              manage personal information in connection with our online, mobile and
              other digital channels and services. It should be read in conjunction with
              the Legal Notice and Terms of Use.
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>Your consent</h3>
            <p v-t>
              Each time you use our online, mobile, and electronic services, you are
              indicating your acknowledgment and consent to the collection, use and
              disclosure of your personal information as identified in this policy,
              which we may revise periodically without notice. We will let you know of
              any changes by posting the revised policy with a new effective date. If
              you do not accept the terms outlined in this policy, please do not use our
              online, mobile and electronic services.
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>What personal information do we collect?</h3>
            <p v-t>
              We may collect personal information when you use our websites, online,
              mobile, and other electronic services (collectively, digital channels),
              including:
            </p>
            <ul>
              <li>
                <strong v-t>Personal information you provide</strong>&nbsp;
                <span v-t>
                  like account and
                  contact information, including your name, email address, user ID,
                  password, and service preferences. Refer to your specific app for
                  additional, applicable information.
                </span>
              </li>
              <li>
                <strong v-t>Financial information</strong> &#8211;&nbsp;
                <span v-t>
                  that you provide to us at
                  the time of account creation of an account through our online or mobile
                  account creation process
                </span>
              </li>
              <li>
                <strong v-t>Transactional data about your use of our products and services</strong>
              </li>
              <li><strong v-t>Mobile phone number</strong></li>
              <li>
                <strong v-t>Device-related information</strong> <span v-t>like</span>:
                <ul>
                  <li>
                    <strong v-t>Internet Protocol (IP) address</strong> &#8211;&nbsp;
                    <span v-t>
                      An IP address is a unique set of numbers assigned by Internet Service
                      Providers (ISP) to all devices used to access the internet. While CI
                      is not able to identify an individual using the IP address alone, in
                      certain circumstances, it could be used to identify an individual
                      using the site.
                    </span>
                  </li>
                  <li>
                    <strong v-t>Mobile country code</strong>&nbsp;
                    <span v-t>
                      which is a three-digit number
                      that indicates the country in which your mobile device is registered
                      (for example, 302 is the mobile country code for Canada)
                    </span>
                  </li>
                  <li>
                    <strong v-t>Geolocation</strong>&nbsp;
                    <span v-t>
                      (if enabled on your device) provides the approximate physical location of your device
                    </span>
                  </li>
                  <li>
                    <strong v-t>Device ID</strong> <span v-t>which is a unique number that identifies your device</span>
                  </li>
                  <li><strong v-t>Make and model</strong> <span v-t>of your device</span></li>
                  <li><strong v-t>Operating system</strong> <span v-t>of your device</span></li>
                  <li><strong v-t>Information about screen size</strong> <span v-t>of your device</span></li>
                  <li><strong v-t>Date and time of your use of our app</strong></li>
                  <li><strong v-t>Browser data</strong></li>
                  <li><strong v-t>Software applications data</strong></li>
                  <li><strong v-t>Mobile device identification number</strong> <span v-t>(IMEI)</span></li>
                  <li>
                    <strong v-t>Mobile Marketing ID</strong> <span v-t>(if enabled) such as AAID and IDFA</span>
                  </li>
                </ul>
              </li>
              <li>
                <strong v-t>Statistical information about how users interact with our digital channels</strong>,&nbsp;
                <span v-t>such as average time spent on a specific mobile or web page</span>
              </li>
              <li>
                <strong v-t>Website use information</strong>,&nbsp;
                <span v-t>
                  such as browsing behaviour on
                  our websites and links, location you click, form data and downloads, as
                  well as other data gathered from the use of web tools (for example,
                  Cookies, Web Beacons, Tagging)
                </span>
              </li>
              <li>
                <strong v-t>Other information</strong>, <span v-t>such as any feedback you may provide us</span>
              </li>
            </ul>
          </div>
          <div class="policy-sections">
            <h3 v-t>For what purposes do we use and disclose your personal information?</h3>
            <p v-t>We may use or disclose this information collected online to:</p>
            <ul>
              <li v-t>Verify your identity and authenticate you during online sessions</li>
              <li v-t>Provide and manage the online products and services</li>
              <li v-t>
                Provide you with services and offers compatible with your device and
                relevant to your region
              </li>
              <li v-t>Determine eligibility of your device</li>
              <li v-t>
                Protect against fraud, security threats, and otherwise manage risks
              </li>
              <li v-t>
                Communicate with you regarding products and services that may be of
                interest, including displaying targeted and relevant advertising, and
                offering you special promotions
              </li>
              <li v-t>
                Evaluate and improve our websites, online apps, mobile apps, and other
                electronic offerings
              </li>
              <li v-t>
                Tailor our services and otherwise enhance the online customer experience
                to better meet your needs through digital channels
              </li>
              <li v-t>
                Perform analytics and measure the effectiveness of our digital channels
                and services, including obtaining insight into how to improve the
                design, content and navigation of our digital channels
              </li>
              <li v-t>Satisfy legal or regulatory requirements</li>
            </ul>
          </div>
          <div class="policy-sections">
            <h3 v-t>Use of web tools</h3>
            <p v-t>
              We and our service providers may use various tracking tools, such as
              Cookies, Pixel Tags and Web Beacons. Through the use of cookies and other
              technologies, we can enhance your experience with improved design,
              personalized content that may be relevant to you, and effective
              navigation. We also use online advertising to reach new potential clients
              and remarketing tools that allow us to target ads to specific groups of
              people who have previously interacted with CI Direct Investing's
              websites.
            </p>
            <ul>
              <li>
                <strong v-t>Cookies</strong> &#8211;&nbsp;
                <span v-t>
                  A Cookie is a string of data containing
                  a unique identification number that our website sends to your computer’s
                  web browser. Wealthbar Financial Services Inc. uses cookies and online
                  tools, such as Google Analytics, to collect non-personal information to
                  understand how people are using the Services and how Wealthbar Financial
                  Services Inc. can make them better. Most browsers and mobile devices
                  offer their own settings to manage cookies. You can refuse or delete
                  cookies. Please refer to your browser’s online help for further
                  instruction and information on setting Cookie preferences.
                </span>
                <p>
                  <span v-t>
                    If you do not accept any category of Cookies, you may continue to
                    browse our websites; however, please be aware that certain features of
                    our website may not function properly without Cookies. You should note
                    that if you would like certain services, you will be required to
                    accept the Cookies to maintain session integrity and enhanced
                    security. Additional resources that may assist you in managing Cookies
                    used for advertising include the
                  </span>&nbsp;
                  <a v-t href="https://youradchoices.ca/">Digital Advertising Alliance (DAA)</a>.
                </p>
                <p v-t>
                  If you refuse a cookie when using the Services, or if you delete
                  cookies, you may experience some inconvenience in your use of the
                  Services. For example, you may not be able to sign in and access your
                  account, or Wealthbar Financial Services Inc. may not be able to
                  recognize.
                </p>
              </li>
              <li>
                <strong v-t>Tagging</strong> &#8211;&nbsp;
                <span v-t>
                  Tagging software is customized code on
                  our websites, designed to monitor user activity on our websites, to help
                  us in presenting users with content relevant to their use of our
                  website.
                </span>
              </li>
              <li>
                <strong v-t>Web Beacons</strong> &#8211;&nbsp;
                <span v-t>
                  Web beacons are small and
                  transparent images embedded in web pages and e-mail communications that,
                  when combined with Cookies, help provide us with information about the
                  use and effectiveness of a website or e-mail communication. As an
                  example, we may include web beacons within e-mail communications to
                  receive confirmation of receipt of an e-mail communication or clicks on
                  links within our websites.
                </span>
              </li>
            </ul>
          </div>
          <div class="policy-sections">
            <h3 v-t>Your choices</h3>
            <ul>
              <li>
                <strong v-t>Email communications</strong> &#8211;&nbsp;
                <span v-t>
                  Wealthbar Financial
                  Services Inc. uses electronic messages and e-mail to contact clients
                  about information relating to Wealthbar Financial Services Inc.,
                  including special offers, products and services. You may withdraw you
                  consent at any time by clicking an unsubscribe link provided in each
                  e-mail I receive or by sending an unsubscribe request by
                </span>
                &nbsp;
                <a v-t href="#contact-us">contacting us</a>.
              </li>
              <li>
                <strong v-t>Voluntary participation</strong> &#8211;&nbsp;
                <span v-t>
                  You may choose to enter
                  your information to participate in online surveys, feedback, contests,
                  or other activities we sponsor. We use this information for a number of
                  purposes, such as assessing the performance of the website, or improving
                  products and services, or identifying products and services that might
                  interest you. This information will be used for the purposes outlined in
                  the Privacy Notice. If you do not want your information used for the
                  disclosed purposes, do not participate in these voluntary activities.
                </span>
              </li>
              <li>
                <strong v-t>Educational webinars</strong> &#8211;&nbsp;
                <span v-t>
                  You may choose to enter
                  your information to participate in online meetings, educational events
                  that we sponsor. We use this information for a number of purposes, such
                  as assessing the performance of the website, or improving products and
                  services, or issuing continuing education credits. This information will
                  be used for the purposes outlined in the Privacy Notice. The information
                  you enter may be transmitted through or to our 3rd party providers of
                  webinars. If you do not want your information used for the disclosed
                  purposes, do not participate in these voluntary activities.
                </span>
              </li>
              <li>
                <strong v-t>Use of electronic signature</strong> &#8211;&nbsp;
                <span v-t>
                  In the event you
                  choose to utilize electronic signature features included in any of our
                  online Services, you agree to the collection and retention of your IP
                  address, e-mail address and activity records for the purposes of
                  identifying you, maintaining evidence of transaction authorization,
                  fraud protection and security.
                </span>
              </li>
            </ul>
          </div>
          <div class="policy-sections">
            <h3 v-t>Safeguards</h3>
            <h4 v-t>Our security measures</h4>
            <p v-t>
              We take our responsibility to safeguard customer information very
              seriously. To protect certain communications between your computer and our
              servers, we use a technology solution called "Secure Sockets Layer" (SSL).
              SSL ensures that the information transmitted is encrypted. In addition, we
              use the concept of "defense in depth", in our cybersecurity program that
              has many layers, from employee awareness to having firewalls at the very
              edge of our network.
            </p>
          </div>
          <div class="policy-sections">
            <h4 v-t>Your security measures</h4>
            <p>
              <span v-t>
                You are responsible for ensuring the security of your unique web login ID
                and password. You must avoid selecting an obvious password, such as a
                street address, date of birth, or telephone number and to change your
                password on a regular basis, in order to reduce the potential for
                unauthorized use. Any web login ID, password, or any other piece of
                information chosen by you or provided to you as part of Wealthbar
                Financial Services Inc.’s security procedures, must be treated as
                confidential and personal to you, and you must not disclose it to any
                other person or entity.
              </span>
              <br>
              <span v-t>
                Do not respond to text messages, pop-ups, emails, or other internet
                requests that ask you to reveal personal information about yourself or
                your account when using the Services. Wealthbar Financial Services Inc.
                will never send you unsolicited messages or emails asking for your
                password, credit card, account numbers, etc. Wealthbar Financial Services
                Inc. will never ask you to validate or restore your account or access to
                the Services through unsolicited messages or emails.
              </span>
            </p>
          </div>
          <div class="policy-sections">
            <h4 v-t>Third-party links</h4>
            <p v-t>
              For your convenience, the Services may provide links or pointers to
              third-party sites. You are subject to any terms and conditions of such
              third-party sites. Non- Wealthbar Financial Services Inc. websites are not
              subject to Wealthbar Financial Services Inc.’s privacy policy or security
              standards.
            </p>
          </div>
          <div class="policy-sections">
            <h2 id="contact-us" v-t>Contact us</h2>
            <p v-t>
              If you have any questions or concerns about our privacy practices, the
              privacy of your personal information, or you want to change your privacy
              preferences, please let us know. We are committed to helping resolve your
              questions or concerns.
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>Step 1: Voice your question or concern</h3>
            <p>
              <span v-t>
                The best way to address the concern or question for existing clients, is
                to login and submit your request through our messaging platform, or by
                email to
              </span>&nbsp;
              <a href="mailto:support@cidirectinvesting.com">support@cidirectinvesting.com</a>.
              &nbsp;
              <span v-t>
                If you are not satisfied with the answer or resolution provided in Step
                1, please proceed to Step 2.
              </span>
            </p>
          </div>
          <div class="policy-sections">
            <h3 v-t>Step 2: Escalate your concern to the Privacy Office</h3>
            <p v-t>You can escalate your concern in the following ways:</p>
            <ol>
              <li v-t>
                Ask the individual you are dealing with in Step 1 to escalate on your
                behalf.
              </li>
              <li v-t>
                Escalate the concern directly with the Privacy Office, using the
                following contact information.
              </li>
            </ol>

            <table class="table table-hover">
              <thead>
                <tr>
                  <th v-t>CI Financial Group</th>
                  <th v-t>Privacy Officer Contact</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-t>WealthBar Financial Services Inc.</td>
                  <td>
                    <span v-t>Privacy Office</span><br>
                    <span v-t>2 Queen Street East</span><br>
                    <span v-t>19th Floor</span><br>
                    Toronto, Ontario<br>
                    M5C 3G7<br>
                    <br>
                    <span v-t>Phone: 1-800-792-9355</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="policy-sections">
            <h3 v-t>Step 3: Further Escalation</h3>
            <p v-t>
              If after contacting us you do not feel that your concern has been
              adequately addressed, you may wish to contact:
            </p>
            <p>
              <span v-t>The Office of the Privacy Commissioner of Canada</span><br>
              <span v-t>Call: 1-800-282-1376</span><br>
              <span v-t>Visit:</span> <a href="http://www.priv.gc.ca">www.priv.gc.ca</a>
            </p>
          </div>
        </section>
      </div>
    </div>
    <public-footer />
  </main>
</template>

<script>
import { publicBanner, publicFooter } from '@/components/layout';

export default {
  head() {
    return {
      title: this.$t('Privacy Policy'),
    };
  },

  components: {
    publicBanner,
    publicFooter,
  },
};
</script>

<style scoped lang="scss">
  main {
    background-color: $primary-50;

    section {
      background-color: $white;
    }
  }

  .banner {
    border-bottom: 1px solid $pearl-300;
  }

  .policy-container {
    padding-bottom: 3rem;
    background-color: $white;

    .container-content {
      width: 100%;
    }
  }

  .privacy {
    padding: 3rem 0;
    text-align: center;
  }

  .privacy-content {
    padding: 0 1rem;
    text-align: left;

    @media #{$screen-width-small} {
      margin: 0 auto;
      max-width: 38em;
      width: 100%;
    }

    .policy-sections {
      padding: 0.5rem 0;

      &:first-child {
        padding-top: 0;
      }

      &:last-child {
        padding-bottom: 0;
      }

      h2 { padding: 0.75rem 0; }

      h3, h4 {
        padding-bottom: 0.75rem;
      }

      h2 + p,
      h3 + p,
      h4 + p {
        margin-bottom: 1rem;
      }

      ul {
        margin: 0.5rem 1rem;

        li {
          list-style-type: disc;

          ul {
            list-style: disc;
            li {
              list-style-type: disc;
            }
          }
        }
      }

      .table {
        border-spacing: 0;
        border-collapse: collapse;
        width: 100%;
        max-width: 100%;
        margin-bottom: 20px;
        thead {
          tr {
            th {
              border-top: 0;
              vertical-align: bottom;
              border-bottom: 1px solid #efefef;
              padding: 8px;
              line-height: 1.42857143;
              text-align: left;
              font-weight: bold;
            }
          }
        }
        tbody {
          tr {
            td {
              padding: 8px;
              line-height: 1.42857143;
              vertical-align: top;
              border-bottom: 1px solid #efefef;
            }
          }
        }
        &.table-hover {
          tbody {
            tr:hover {
              background-color: #f5f5f5;
            }
          }
        }
      }
    }
  }
</style>
